import { looseToNumber } from '@develop/ia-utils'
import { color } from 'console-log-colors'
import type { RouteLocationNormalized } from '#vue-router'
import { IaCookie } from '~/models/cookie'
import type { GermanOrEnglishGermanRoute } from '~/models/middleware-types'

// Routes to be matched [en, de], that have the pharmacy version of the page
const pharmacyRelatedPages = {
  'homepage': [
    { name: 'index' },
  ],
  'prescription-upload': [
    { name: 'prescription-upload' },
    { name: 'rezept-upload' },
  ],
  'products': [
    { name: 'products' },
    { name: 'produkte' },
  ],
  'product': [
    { name: 'product-productName-pzn' },
    { name: 'produkt-productName-pzn' },
  ],
  'express-delivery': [
    { name: 'express-delivery' },
    { name: 'express-botendienst' },
  ],
  'topic-slug': [
    { name: 'topic-slug' },
    { name: 'themenuebersicht-slug' },
  ],
  'brand-slug': [
    { name: 'brand-slug' },
    { name: 'markenuebersicht-slug' },
  ],
} as const satisfies Record<string, GermanOrEnglishGermanRoute>

type PharmacyRelatedPageKey = keyof typeof pharmacyRelatedPages

const matchPharmacyRelatedPage = (page: PharmacyRelatedPageKey, to: RouteLocationNormalized) => {
  return pharmacyRelatedPages[page].some(route => route.name === to.name)
}

/**
 * Checks if it is a page that is pharmacy-related but does not have a pharmacy slug in the URL
 */
const isPharmacyRelatedPageWithoutPharmacy = (to: RouteLocationNormalized) => {
  const pages = Object.values(pharmacyRelatedPages).flat()
  return pages.some(route => route.name === to.name)
}

/**
 * Ensures navigation to pharmacy version of pharmacy-related page, taking into account the selected pharmacy
 */
export default defineNuxtRouteMiddleware(async (to) => {
  onShowLogs(() => console.info(color.cyan('[03] pharmacy-related\n'), to.path))

  // If user navigates to a page that is not pharmacy-related without pharmacy, we don't need to check anything
  if (!isPharmacyRelatedPageWithoutPharmacy(to)) {
    onShowLogs(() => console.info(color.yellow(`Current page ${to.name} is not pharmacy-related page without pharmacy`)))
    return
  }

  const pharmacyIdCookie = useCookie<string | undefined>(IaCookie.SelectedPharmacy)
  const pharmacyId = looseToNumber(pharmacyIdCookie.value)

  if (!pharmacyId) {
    return
  }

  // TODO: not needed for non fullPharmacySlug routes
  const { getPharmacyParamsWithStatus } = useRoutingValidator()
  const pharmacyParamsWithStatus = await getPharmacyParamsWithStatus(pharmacyId)

  if (!pharmacyParamsWithStatus.params) {
    return
  }

  const pharmacyParams = ensurePharmacyParamsType(pharmacyParamsWithStatus.params)

  const router = useRouter()
  let route: RouteLocationRaw | undefined

  const isPage = (key: PharmacyRelatedPageKey) => matchPharmacyRelatedPage(key, to)

  // --------------------------------------------------------------------------
  // Homepage
  // --------------------------------------------------------------------------
  if (isPage('homepage')) {
    route = router.resolve({
      name: resolvePharmacyRouteName('apotheke-pharmacyName-pharmacyPlz-pharmacyId', pharmacyParams),
      params: pharmacyParams,
    })
  }

  // --------------------------------------------------------------------------
  // Prescription Upload
  // --------------------------------------------------------------------------
  else if (isPage('prescription-upload')) {
    route = router.resolve({
      name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-rezept-upload', pharmacyParams),
      params: pharmacyParams,
    })
  }

  // --------------------------------------------------------------------------
  // Products
  // --------------------------------------------------------------------------
  else if (isPage('products')) {
    route = router.resolve({
      name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-produkte', pharmacyParams),
      params: pharmacyParams,
    })
  }

  // --------------------------------------------------------------------------
  // Product
  // --------------------------------------------------------------------------
  else if (isPage('product')) {
    const specificRoute = pharmacyRelatedPages.product.at(-1)!.name
    const toParams = router.resolve<typeof specificRoute>(to).params

    route = router.resolve({
      name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-produkt-productName-pzn', pharmacyParams),
      params: {
        ...pharmacyParams,
        productName: toParams.productName,
        pzn: toParams.pzn,
      },
    })
  }

  // --------------------------------------------------------------------------
  // Express Delivery
  // --------------------------------------------------------------------------
  else if (isPage('express-delivery')) {
    route = router.resolve({
      name: 'apotheke-pharmacyId-express-botendienst',
      params: {
        pharmacyId: pharmacyParams.pharmacyId,
      },
    })
  }

  // --------------------------------------------------------------------------
  // Topic (Themenuebersicht)
  // --------------------------------------------------------------------------
  else if (isPage('topic-slug')) {
    const specificRoute = pharmacyRelatedPages['topic-slug'].at(-1)!.name
    const toParams = router.resolve<typeof specificRoute>(to).params

    route = router.resolve({
      name: 'apotheke-pharmacyId-themenuebersicht-slug',
      params: {
        pharmacyId: pharmacyParams.pharmacyId,
        slug: toParams.slug,
      },
    })
  }

  // --------------------------------------------------------------------------
  // Brand (Markenuebersicht)
  // --------------------------------------------------------------------------
  else if (isPage('brand-slug')) {
    const specificRoute = pharmacyRelatedPages['brand-slug'].at(-1)!.name
    const toParams = router.resolve<typeof specificRoute>(to).params

    route = router.resolve({
      name: 'apotheke-pharmacyId-markenuebersicht-slug',
      params: {
        pharmacyId: pharmacyParams.pharmacyId,
        slug: toParams.slug,
      },
    })
  }

  if (route) {
    return customNavigateTo(route, { redirectCode: 302 })
  }

  return abortNavigation({
    statusCode: 404,
    message: 'Pharmacy related page not found',
  })
})
